import "./Header.css";
import React, { useEffect, useCallback, useMemo, useRef } from "react";
import * as _ from "lodash";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import { debounce } from "lodash";
import MenuItem from "@mui/material/MenuItem";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useOktaAuth } from "@okta/okta-react";
import { useLocation } from "react-router-dom";
import HeaderSearch from "../headerSearch/HeaderSearch";
import { AppState } from "../../AppState/appState";
import { useDispatch, useSelector } from "react-redux";
import type { Action } from "redux";
import TerminalPicker from "../terminalPicker/TerminalPicker";
import { APP, ETab, STORAGE_KEYS } from "../../constants";

import {
  IFilterForm,
  TTerminal,
  saveOveragesFilterForm,
} from "../../AppState/overagesFilterFormActions";
import { saveMyAssignmentsFilterForm } from "../../AppState/myAssignmentsFilterFormActions";
import { saveShortagesFilterForm } from "../../AppState/shortagesFilterFormAction";
import { deploymentStatusApi } from "../../API/deploymentStatus";
import { saveDamagesFilterForm } from "../../AppState/damagesFilterFormAction";
import { saveOnHandDeliveryFilterForm } from "../../AppState/onHandDeliveryFilterFormAction";
import { saveExceptionSearchFilterForm } from "../../AppState/exceptionSearchFilterFromAction";
import { filterFromSelector } from "../../AppState/selectors";
import { saveMisdeliveryFilterForm } from "../../AppState/misdeliveryFilterFormAction";
import { getUserInfo, redirectToOktaLogin } from "../../helpers";

const Header = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { oktaAuth } = useOktaAuth();
  const isMountedRef = useRef(true);

  const path = location.pathname.toString();
  const paths = ["manifest", "exception", "apb/send", "entry", "send-email"];
  const ShowFilterpaths = ["exceptions/search"];

  const containsPath = paths.some((keyword) => path.includes(keyword));
  const containsShowFilterPath = ShowFilterpaths.some((keyword) => path.includes(keyword));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchValue, setSearchValue] = React.useState<string>("");
  
  const currentTerminalValue: TTerminal = useSelector(
    (state: AppState) => state.activeTerminal
  );

  const currentTabPosition = useSelector(
    (state: AppState) => state.tabPosition
  );
  const isLoggedIn: boolean = useSelector(
    (state: AppState) => state.isLoggedIn
  );
  const filterForm: IFilterForm = useSelector(filterFromSelector);

  const open = Boolean(anchorEl);

  // Safe localStorage access with null handling
  const oktaUser = useMemo(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.OKTA_TOKEN_STORAGE);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Failed to parse Okta token from localStorage:", error);
      return null;
    }
  }, []);

  // Get authUser from localStorage (recompute when isLoggedIn changes as that's when user data is stored)
  const authUser = useMemo(() => {
    const storedUser = getUserInfo();
    if (storedUser && storedUser !== "undefined") {
      return storedUser;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Compute userName: Priority 1) authUser, 2) Okta token, 3) default
  const userName = useMemo(() => {
    // First try to get from authUser (JWT user data from DB) - use name field for display
    if (authUser?.name) {
      return authUser.name;
    }
    
    // Fallback to Okta token claims
    const oktaName = _.get(oktaUser, "idToken.claims.name");
    if (oktaName) {
      return oktaName;
    }
    
    // Final fallback
    return "Saia User";
  }, [authUser, oktaUser]);
  const handleClick = (event: {
    currentTarget: React.SetStateAction<HTMLElement | null>;
  }) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // No token validation logic needed here - RequiredAuth handles it
  // Header is now a pure UI component that displays based on Redux state

  const logout = useCallback(() => {
    redirectToOktaLogin(oktaAuth, "User initiated logout");
  }, [oktaAuth]);

  const debouncedSave = useMemo(
    () =>
      debounce(
        (newValue: string, currentTab: number) =>
          onSearchChange(newValue, currentTab),
        1000
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterForm]
  );

  const updateValue = (newValue: string) => {
    setSearchValue(newValue);
    debouncedSave(newValue, currentTabPosition);
  };

  const onSearchChange = useCallback((value: string, currentTab: number = 1) => {
    const filterFormParam: IFilterForm = structuredClone(filterForm ?? {});
    filterFormParam["search"] = value;

    const actionMap: Record<number, (payload: IFilterForm) => Action> = {
      [ETab.overages]: saveOveragesFilterForm,
      [ETab.myAssignments]: saveMyAssignmentsFilterForm,
      [ETab.shortages]: saveShortagesFilterForm,
      [ETab.damages]: saveDamagesFilterForm,
      [ETab.onHandDelivery]: saveOnHandDeliveryFilterForm,
      [ETab.dashboard]: saveExceptionSearchFilterForm,
      [ETab.misdelivery]: saveMisdeliveryFilterForm,
    };

    const action = actionMap[currentTab];
    if (action) {
      dispatch(action(filterFormParam));
    }
  }, [filterForm, dispatch]);

  // Show page-wise submitted search value on header
  useEffect(() => {
    setSearchValue(filterForm.search || "");
  }, [currentTabPosition, filterForm.search]);

  // Clear search when terminal changes
  useEffect(() => {
    setSearchValue("");
  }, [currentTerminalValue]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  // Check deployment status when user logs in
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        await deploymentStatusApi();
      } catch (error) {
        console.error("Deployment status check failed:", error);
      }
    };

    if (isLoggedIn) {
      checkMaintenanceMode();
    }
  }, [isLoggedIn]);

  return (
    <div>
      <AppBar position="static">
        <Toolbar>
          <Grid container>
            <Grid item md={8} xs={8}>
              <Grid display="flex" justifyContent="flex-start">
                <img src="/Saia_ltl.png" alt="Saia_ltl" />

                {/* Search Filter  */}
                {((APP.showHeaderSearchPage.includes(currentTabPosition) &&
                  !containsPath) || containsShowFilterPath) && (
                    <HeaderSearch
                      onSearchChange={(searchText: string) => {
                        const trimmed = searchText.trimStart();
                        if (trimmed !== " ") {
                          updateValue(trimmed);
                        }
                      }}
                      value={searchValue}
                    />
                  )}
              </Grid>
            </Grid>
            {/* Group Filters  */}
            {![ETab.maintenance, ETab.unauthorized].includes(
              currentTabPosition
            ) && (
              <>
                <Grid item md={2} xs={2} className="border-right">
                  {isLoggedIn &&
                    APP.showTerminalPickerPage.includes(currentTabPosition) &&
                    !containsPath && (
                      <div data-testid="terminal-picker">
                        {authUser?.username ? <TerminalPicker /> : <>..</>}
                      </div>
                    )}
                </Grid>

                {/* User Name  */}

                <Grid item md={2} xs={2}>
                  <Box
                    display="flex"
                    className="account-user"
                    justifyContent="flex-end"
                  >
                    <Button
                      id="demo-positioned-button"
                      aria-controls={open ? "demo-positioned-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? "true" : undefined}
                      onClick={handleClick}
                      endIcon={<KeyboardArrowDownIcon />}
                    >
                      {userName}
                    </Button>
                    <Menu
                      id="demo-positioned-menu"
                      aria-labelledby="demo-positioned-button"
                      className="submenu"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      slotProps={{
                        paper: {
                          style: {
                            width: "135px",
                            transform: "translateX(11%) translateY(0%)",
                            left: "0",
                            fontSize: "0.5rem",
                          },
                        },
                      }}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem onClick={logout}>Logout</MenuItem>
                    </Menu>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </Toolbar>
      </AppBar>
    </div>
  );
};
export default Header;
